import { Link } from 'react-router-dom';
import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Crown, Search, Shield, Trophy, Users, X } from 'lucide-react';
import { RATING_CONFIG } from '../../config';
import PodiumSparks from './PodiumSparks';
import styles from './ClanRating.module.css';

const API = `${RATING_CONFIG.apiUrl.replace(/\/$/, '')}/clans`;
const PAGE_SIZE = 20;
const number = (value) => Number(value ?? 0).toLocaleString('ru-RU');
const safeColor = (value) => /^(#[\da-f]{6}|white|red|green|blue|yellow|orange|purple|pink|cyan|gold|gr[ae]y|aqua|lime|magenta)$/i.test(value || '')
  ? value : '#A5B6CE';

function useClanRequest(url) {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ url: null, data: null, error: null, loading: true });
  useEffect(() => {
    if (!url) return undefined;
    const controller = new AbortController();
    setState({ url, data: null, error: null, loading: true });
    fetch(url, { signal: controller.signal })
      .then((response) => {
        if (response.status === 404) throw new Error('Клан не найден. Возможно, он был распущен.');
        if (!response.ok) throw new Error('Не удалось загрузить данные кланов');
        return response.json();
      })
      .then((data) => {
        if (!controller.signal.aborted) setState({ url, data, error: null, loading: false });
      })
      .catch((error) => {
        if (!controller.signal.aborted) setState({ url, data: null, error: error.message, loading: false });
      });
    return () => controller.abort();
  }, [url, attempt]);
  return {
    ...(state.url === url ? state : { data: null, error: null, loading: true }),
    retry: () => setAttempt((value) => value + 1),
  };
}

function RequestState({ request, children }) {
  if (request.loading) return <div className={styles.state} role="status">Загрузка кланов…</div>;
  if (request.error) return (
    <div className={styles.state} role="alert">
      <p>{request.error}</p>
      <button type="button" className={styles.action} onClick={request.retry}>Повторить</button>
    </div>
  );
  return children;
}

function ClanIdentity({ clan, detail = false, podium = false }) {
  const Heading = detail ? 'h2' : 'span';
  return (
    <div className={styles.identity}>
      <span className={styles.emblem} style={{ color: safeColor(clan.color) }}>
        <Shield size={detail ? 36 : podium ? 48 : 28} aria-hidden="true" />
      </span>
      <div className={styles.identityText}>
        <span className={styles.tag} style={{ color: safeColor(clan.color) }}>[{clan.suffix}]</span>
        <Heading className={styles.name}>{clan.name}</Heading>
      </div>
    </div>
  );
}

function ClanStats({ clan }) {
  return (
    <div className={styles.stats}>
      <span><Trophy size={16} aria-hidden="true" /><strong>{number(clan.score)}</strong><span>Очки клана</span></span>
      <span><Shield size={16} aria-hidden="true" /><strong>{number(clan.level)}</strong><span>Уровень</span></span>
      <span><Users size={16} aria-hidden="true" /><strong>{number(clan.memberCount)}</strong><span>Участники</span></span>
    </div>
  );
}

function ClanCard({ clan, onOpen, podium = false }) {
  const variant = ['gold', 'silver', 'bronze'][clan.rank - 1];
  return (
    <button type="button" className={`${styles.card} ${podium ? styles.podiumCard : ''}`} data-rank={clan.rank}
      aria-label={`Открыть клан ${clan.name}`} onClick={() => onOpen(clan.clanId)}>
      {podium && <PodiumSparks variant={variant} />}
      <span className={styles.cardContents}>
        <span className={styles.cardTop}>
          <span className={styles.rank}>{clan.rank <= 3 ? ['🥇', '🥈', '🥉'][clan.rank - 1] : `#${clan.rank}`}</span>
          {!podium && <span className={styles.open}>Состав <ArrowRight size={16} aria-hidden="true" /></span>}
        </span>
        <ClanIdentity clan={clan} podium={podium} />
        <span className={styles.leader}>Глава: {clan.leader}</span>
        <ClanStats clan={clan} />
        {podium && <span className={styles.open}>Открыть состав <ArrowRight size={16} aria-hidden="true" /></span>}
      </span>
    </button>
  );
}

function MemberAvatar({ username }) {
  const [failed, setFailed] = useState(false);
  return failed ? (
    <span className={styles.avatarFallback} aria-hidden="true">{username?.charAt(0).toUpperCase() || '?'}</span>
  ) : (
    <img className={styles.avatar} src={`${RATING_CONFIG.avatarUrl}/${encodeURIComponent(username)}`}
      alt="" width={40} height={40} loading="lazy" onError={() => setFailed(true)} />
  );
}

function ClanDetails({ data }) {
  const [search, setSearch] = useState('');
  const headingRef = useRef(null);
  useEffect(() => { headingRef.current?.focus(); }, []);
  const { clan, members = [] } = data;
  const filtered = members.filter((member) => member.username.toLocaleLowerCase('ru-RU').includes(search.trim().toLocaleLowerCase('ru-RU')));
  return (
    <article className={styles.detail}>
      <div className={styles.detailHeading} ref={headingRef} tabIndex={-1}>
        <ClanIdentity clan={clan} detail />
        <span className={styles.rank}>#{clan.rank} в рейтинге</span>
      </div>
      {clan.description && <p className={styles.description}>{clan.description}</p>}
      <ClanStats clan={clan} />
      <div className={styles.membersHeading}>
        <h3>Состав клана <span>{number(members.length)}</span></h3>
        <label className={styles.memberSearch}>
          <Search size={18} aria-hidden="true" />
          <input type="search" value={search} onChange={(event) => setSearch(event.target.value)}
            placeholder="Найти участника" aria-label="Найти участника клана" />
        </label>
      </div>
      {filtered.length ? (
        <div className={styles.membersTableWrap}>
          <table className={styles.membersTable}>
            <thead><tr><th scope="col">Игрок</th><th scope="col">Титул в клане</th></tr></thead>
            <tbody>{filtered.map((member, index) => (
              <tr key={`${member.username}-${index}`}>
                <td><div className={styles.memberIdentity}>
                  <MemberAvatar username={member.username} />
                  <div className={styles.memberInfo}>
                    <span className={styles.memberName}>
                      <Link to={`/players/${encodeURIComponent(member.username)}`}>{member.username}</Link>
                      {member.role === 'LEADER' && <Crown size={18} className={styles.crown} aria-label="Глава клана" />}
                    </span>
                    <span className={styles.memberProgress}>
                      <span>Уровень {member.level == null ? '—' : number(member.level)}</span>
                      <span>Престиж {member.prestige == null ? '—' : number(member.prestige)}</span>
                    </span>
                  </div>
                </div></td>
                <td><span className={styles.role} style={{ color: safeColor(member.roleColor) }}>{member.roleName}</span></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      ) : <p className={styles.state}>{search ? 'Участник не найден' : 'В клане пока нет участников'}</p>}
    </article>
  );
}

export default function ClanRating() {
  const [params, setParams] = useSearchParams();
  const selectedId = params.get('clan');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  useEffect(() => {
    const timer = setTimeout(() => { setSearch(searchInput.trim()); setPage(0); }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);
  const query = new URLSearchParams({ page: String(page), size: String(PAGE_SIZE) });
  if (search) query.set('search', search);
  const listRequest = useClanRequest(selectedId ? null : `${API}?${query}`);
  const detailRequest = useClanRequest(selectedId ? `${API}/${encodeURIComponent(selectedId)}` : null);
  const selectClan = (id) => {
    const next = new URLSearchParams(params);
    if (id == null) next.delete('clan');
    else next.set('clan', String(id));
    setParams(next);
  };

  if (selectedId) return (
    <section aria-label="Информация о клане">
      <button type="button" className={styles.back} onClick={() => selectClan(null)}>
        <ArrowLeft size={18} aria-hidden="true" /> Все кланы
      </button>
      <RequestState request={detailRequest}>
        {detailRequest.data && <ClanDetails key={selectedId} data={detailRequest.data} />}
      </RequestState>
    </section>
  );

  const { clans = [], totalPages = 0, totalElements = 0 } = listRequest.data || {};
  const showPodium = page === 0 && !search && !searchInput.trim() && clans.length >= 3;
  return (
    <section aria-label="Рейтинг кланов">
      <p className={styles.hint}>Топ по суммарным очкам клана. Откройте клан, чтобы посмотреть состав и титулы участников.</p>
      <div className={styles.search} role="search" aria-label="Поиск клана">
        <Search size={20} aria-hidden="true" />
        <input type="search" placeholder="Найти клан по названию или тегу" aria-label="Название или тег клана"
          value={searchInput} onChange={(event) => setSearchInput(event.target.value)} />
        {searchInput && <button type="button" onClick={() => setSearchInput('')} aria-label="Очистить поиск клана"><X size={18} /></button>}
      </div>
      <RequestState request={listRequest}>
        {clans.length ? <>
          {showPodium && <div className={styles.podium} role="group" aria-label="Три лучших клана">
            {clans.slice(0, 3).map((clan) => (
              <ClanCard key={clan.clanId} clan={clan} onOpen={selectClan} podium />
            ))}
          </div>}
          <div className={styles.grid}>
            {(showPodium ? clans.slice(3) : clans).map((clan) => (
              <ClanCard key={clan.clanId} clan={clan} onOpen={selectClan} />
            ))}
          </div>
          {totalPages > 1 && <nav className={styles.pagination} aria-label="Страницы рейтинга кланов">
            <button type="button" className={styles.action} disabled={page === 0} onClick={() => setPage(page - 1)} aria-label="Предыдущая страница кланов">←</button>
            <span>Страница {page + 1} из {totalPages}</span>
            <button type="button" className={styles.action} disabled={page + 1 >= totalPages} onClick={() => setPage(page + 1)} aria-label="Следующая страница кланов">→</button>
          </nav>}
          <p className={styles.hint}>Всего кланов: {number(totalElements)}</p>
        </> : <div className={styles.state}><h2>{search ? 'Кланы не найдены' : 'Кланов пока нет'}</h2></div>}
      </RequestState>
    </section>
  );
}
